/* Phone field of the contact form (sections/contact-form.liquid): a country
   code (+34) and the number, sent together as one value ("+34 600 123 456")
   through a hidden input, so the email MUBA receives looks as before.
   The number gets its spaces as the visitor types, whatever spaces, dashes
   or dots they type themselves, loses a leading 0 (kept for Italy), and is
   checked against the country. "+34 600..." or "0034 600..." typed or pasted
   into the number moves the code into the code box. */
(() => {
  // ITU calling codes are prefix-free: 1 and 7 have one digit, these have
  // two, every other code has three.
  const TWO_DIGIT_CODES = new Set([
    '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46',
    '47', '48', '49', '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63',
    '64', '65', '66', '81', '82', '84', '86', '90', '91', '92', '93', '94', '95', '98',
  ]);

  // National numbers (without the leading 0): allowed lengths, first digit,
  // and how the digits are grouped. Other countries: 4 to 14 digits, in
  // groups of three.
  const COUNTRIES = {
    1: { lengths: [10], groups: [3, 3, 4] }, // USA, Canada
    33: { lengths: [9], start: /^[1-79]/, groups: [1, 2, 2, 2, 2] }, // France
    34: { lengths: [9], start: /^[6789]/, groups: [3, 3, 3] }, // Spain
    39: { lengths: [6, 7, 8, 9, 10, 11], start: /^[03]/, groups: [3, 3, 4], keepZero: true }, // Italy
    41: { lengths: [9], groups: [2, 3, 2, 2] }, // Switzerland
    44: { lengths: [10], groups: [4, 6] }, // United Kingdom
    49: { lengths: [6, 7, 8, 9, 10, 11, 12, 13], groups: [3, 4, 4] }, // Germany
    351: { lengths: [9], start: /^[29]/, groups: [3, 3, 3] }, // Portugal
  };
  const OTHER = { lengths: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14], groups: [3, 3, 3] };
  const MAX_DIGITS = 14;

  const digitsOf = (value) => value.replace(/\D/g, '');

  const codeLength = (digits) => {
    if (digits[0] === '1' || digits[0] === '7') return 1;
    return TWO_DIGIT_CODES.has(digits.slice(0, 2)) ? 2 : 3;
  };

  const isValidCode = (code) => code.length > 0 && code[0] !== '0' && code.length === codeLength(code);

  const rulesFor = (code) => COUNTRIES[code] || OTHER;

  const nationalDigits = (value, code) => {
    let digits = digitsOf(value);
    if (!rulesFor(code).keepZero) digits = digits.replace(/^0+/, '');
    return digits.slice(0, MAX_DIGITS);
  };

  const format = (digits, code) => {
    const groups = rulesFor(code).groups;
    const parts = [];
    let rest = digits;
    for (let i = 0; rest.length > 0; i++) {
      const size = groups[i] || 3;
      parts.push(rest.slice(0, size));
      rest = rest.slice(size);
    }
    return parts.join(' ');
  };

  // Blank slots in the country's grouping, e.g. "___ ___ ___" for Spain.
  const placeholderFor = (code) => {
    const rules = rulesFor(code);
    const length = rules === OTHER ? 9 : Math.max(...rules.lengths.filter((n) => n <= 10));
    return format('_'.repeat(length), code);
  };

  const isValidNumber = (digits, code) => {
    const rules = rulesFor(code);
    return rules.lengths.includes(digits.length) && (!rules.start || rules.start.test(digits));
  };

  // Puts the caret back behind the same number of digits after reformatting.
  const setValueKeepingCaret = (input, value) => {
    const caret = input.selectionStart ?? input.value.length;
    const digitsBefore = digitsOf(input.value.slice(0, caret)).length;
    input.value = value;
    if (document.activeElement !== input) return;
    let position = 0;
    for (let seen = 0; position < value.length && seen < digitsBefore; position++) {
      if (/\d/.test(value[position])) seen++;
    }
    input.setSelectionRange(position, position);
  };

  class ContactPhone {
    constructor(root) {
      this.root = root;
      this.codeInput = root.querySelector('[data-phone-code]');
      this.numberInput = root.querySelector('[data-phone-number]');
      this.hiddenInput = root.querySelector('[data-phone-value]');
      this.error = root.querySelector('[data-phone-error]');
      this.errorText = this.error.querySelector('[data-phone-error-text]');
      this.touched = false;

      if (this.hiddenInput.value) this.takeInternational(this.hiddenInput.value);

      this.codeInput.addEventListener('input', () => this.onCodeInput());
      this.numberInput.addEventListener('input', () => this.onNumberInput());
      [this.codeInput, this.numberInput].forEach((input) => {
        input.addEventListener('blur', () => {
          if (input.value) this.touched = true;
          this.render();
        });
        input.addEventListener('invalid', () => {
          this.touched = true;
          this.render();
        });
      });
      this.update();
    }

    // "+34 600 123 456" or "0034 600 123 456": code to the code box, the rest
    // to the number.
    takeInternational(value) {
      const digits = digitsOf(value).replace(/^00/, '');
      const code = digits.slice(0, codeLength(digits));
      this.codeInput.value = code;
      this.numberInput.value = format(nationalDigits(digits.slice(code.length), code), code);
    }

    onCodeInput() {
      const code = digitsOf(this.codeInput.value).replace(/^0+/, '').slice(0, 3);
      setValueKeepingCaret(this.codeInput, code);
      this.onNumberInput();
    }

    onNumberInput() {
      const raw = this.numberInput.value.trim();
      if (/^(\+|00)/.test(raw) && digitsOf(raw).replace(/^00/, '').length > 3) {
        this.takeInternational(raw);
      } else {
        const code = this.codeInput.value;
        setValueKeepingCaret(this.numberInput, format(nationalDigits(raw, code), code));
      }
      this.update();
    }

    update() {
      const code = this.codeInput.value;
      this.numberInput.placeholder = placeholderFor(isValidCode(code) ? code : '');
      const digits = digitsOf(this.numberInput.value);
      let codeError = '';
      let numberError = '';

      if (digits) {
        if (!isValidCode(code)) codeError = this.root.dataset.errorCountryCode;
        else if (!isValidNumber(digits, code)) numberError = this.root.dataset.errorInvalid;
      }

      this.codeInput.setCustomValidity(codeError);
      this.numberInput.setCustomValidity(numberError);
      this.message = codeError || numberError;
      this.hiddenInput.value = digits && !this.message ? `+${code} ${this.numberInput.value}` : '';
      this.render();
    }

    render() {
      const show = this.touched && Boolean(this.message);
      this.errorText.textContent = show ? this.message : '';
      this.error.hidden = !show;
      this.codeInput.classList.toggle('contact__phone-input--error', show && Boolean(this.codeInput.validationMessage));
      this.numberInput.classList.toggle('contact__phone-input--error', show && Boolean(this.numberInput.validationMessage));
      if (!this.message) this.touched = false;
    }
  }

  const init = () => document.querySelectorAll('[data-contact-phone]').forEach((root) => {
    if (!root.contactPhone) root.contactPhone = new ContactPhone(root);
  });

  init();
  document.addEventListener('shopify:section:load', init);
})();
